import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Grid,
  Chip
} from '@mui/material';
// Removed unused imports: format, subDays
import axios from 'axios';
import { useAuth } from '../../context/Auth0Context';

const ReportsPage = () => {
  const { isAdmin } = useAuth();

  // Helper function to format decimal hours as "Xh Ym"
  const formatHoursDisplay = (decimalHours) => {
    if (!decimalHours || decimalHours === 0) return '-';
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);

    if (hours === 0) {
      return `${minutes}m`;
    } else if (minutes === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${minutes}m`;
    }
  };
  
  // State for filter values
  const [selectedProject, setSelectedProject] = useState([]);
  const [selectedJobNumber, setSelectedJobNumber] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedProjectStage, setSelectedProjectStage] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedDeliverable, setSelectedDeliverable] = useState('');
  const [selectedJobCode, setSelectedJobCode] = useState('');
  const [activityStartDate, setActivityStartDate] = useState(''); // Activity-specific dates
  const [activityEndDate, setActivityEndDate] = useState(''); // Activity-specific dates
  const [selectedReportType, setSelectedReportType] = useState('');

  // State for data
  const [projects, setProjects] = useState([]);
  const [allJobNumbers, setAllJobNumbers] = useState([]); // Keep all job numbers for search

  // Debug projects state
  console.log('📊 Current projects state:', projects);
  const [users, setUsers] = useState([]);
  const [projectStages, setProjectStages] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [jobCodes, setJobCodes] = useState([]);
  const [filteredJobCodes, setFilteredJobCodes] = useState([]);

  // State for job code search input
  const [jobCodeInput, setJobCodeInput] = useState('');
  const [jobCodeSearchResults, setJobCodeSearchResults] = useState([]);

  // State for preview modal
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [previewType, setPreviewType] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');

  // State for dynamic financial inputs (only for specific reports)
  const [reportFinancials, setReportFinancials] = useState({
    invoicedAmount: '',
    directorComments: ''
  });

  // State for job number filtering
  const [jobNumberInput, setJobNumberInput] = useState('');
  const [filteredJobNumbers, setFilteredJobNumbers] = useState([]);

  // Report type options - Updated to align with client requirements
  const reportTypes = [
    {
      value: 'project_resource_cost',
      label: '1. Project Resource Cost Report',
      description: 'Total time spent on project by various resources multiplied by the relevant resource rate which equates to cost to office without overheads.',
      needsFinancials: false
    },
    {
      value: 'project_cost_with_overhead',
      label: '2. Project Cost With Overhead Report',
      description: 'Add overhead percentage to calculate total office cost from resource costs. Uses overhead percentage stored in project settings.',
      needsFinancials: false
    },
    {
      value: 'invoice_vs_cost_analysis',
      label: '3. Invoice Versus Actual Project Cost',
      description: 'Compare actual invoiced amounts against total office costs including overhead. Manually input invoice amounts for analysis.',
      needsFinancials: ['invoice']
    },
    {
      value: 'activity_duration_trends',
      label: '4. Activity Duration Trends Report',
      description: 'Analyze trends in activity durations (e.g., time spent preparing final accounts or payment certificates).',
      needsFinancials: false
    },
    {
      value: 'wps_missing_entries',
      label: '5. WPS Missing Time Entries',
      description: 'WPS entries where actual hours have not been updated. Uses the same detection data as WPS notifications.',
      needsFinancials: false
    }
  ];

  // No auto-set logic needed since only admins use reports
  // Admin users start with no filters - they can see all data by default

  // Filter job numbers as user types - search through ALL job numbers
  useEffect(() => {
    if (jobNumberInput.trim() === '') {
      setFilteredJobNumbers(allJobNumbers);
    } else {
      const filtered = allJobNumbers.filter(jobNum =>
        jobNum.toString().toLowerCase().includes(jobNumberInput.toLowerCase())
      );
      setFilteredJobNumbers(filtered);
    }
  }, [jobNumberInput, allJobNumbers]);

  // Filter job codes when department changes
  useEffect(() => {
    if (selectedDepartment) {
      const filtered = jobCodes.filter(code => code.department_id === parseInt(selectedDepartment));
      setFilteredJobCodes(filtered);
      // Reset job code selection when department changes
      setSelectedJobCode('');
    } else {
      setFilteredJobCodes(jobCodes);
    }
  }, [selectedDepartment, jobCodes]);

  // Filter job codes as user types
  useEffect(() => {
    if (jobCodeInput.trim() === '') {
      setJobCodeSearchResults([]);
    } else {
      const searchTerm = jobCodeInput.toLowerCase();
      const filtered = filteredJobCodes.filter(code =>
        code.code?.toLowerCase().includes(searchTerm) ||
        code.description?.toLowerCase().includes(searchTerm)
      );
      setJobCodeSearchResults(filtered);
    }
  }, [jobCodeInput, filteredJobCodes]);

  // Define fetch functions first before using them in useEffect
  const fetchData = useCallback(async () => {
    try {
      const requests = [
        axios.get('/api/projects', { withCredentials: true }),
        axios.get('/api/project-job-numbers', { withCredentials: true }),
        axios.get('/api/project-stages', { withCredentials: true }),
        axios.get('/api/departments', { withCredentials: true }),
        axios.get('/api/job-codes', { withCredentials: true })
      ];

      // Always fetch all users since only admins use reports
      requests.push(axios.get('/api/users', { withCredentials: true }));

      const responses = await Promise.all(requests);

      console.log('📊 Reports - Projects data:', responses[0].data);
      setProjects(responses[0].data);
      setAllJobNumbers(responses[1].data); // Store all job numbers for search

      // Filter out any duplicates by ID
      const uniqueProjectStages = Array.from(
        new Map(responses[2].data.map(stage => [stage.id, stage])).values()
      );
      const uniqueDepartments = Array.from(
        new Map(responses[3].data.map(dept => [dept.id, dept])).values()
      );
      const uniqueJobCodes = Array.from(
        new Map(responses[4].data.map(code => [code.id, code])).values()
      );

      setProjectStages(uniqueProjectStages);
      setDepartments(uniqueDepartments);
      setJobCodes(uniqueJobCodes);
      setFilteredJobCodes(uniqueJobCodes); // Initially show all job codes

      // Always set all users since only admins use reports
      setUsers(responses[5].data);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  }, []); // No dependencies needed since the function doesn't use any external variables

  const fetchJobNumbers = useCallback(async (projectId) => {
    try {
      const response = await axios.get(`/api/job-numbers?project_id=${projectId}`, { withCredentials: true });
      console.log('🔍 Job numbers for single project:', response.data);

      // Auto-select the job number(s) from the selected project
      setSelectedJobNumber(response.data);

      console.log('✅ Auto-selected job numbers for single project:', response.data);
    } catch (err) {
      console.error('Error fetching job numbers:', err);
    }
  }, []);

  const fetchJobNumbersForMultipleProjects = useCallback(async (projectIds) => {
    try {
      console.log('🔍 Fetching job numbers for multiple projects:', projectIds);

      // Make individual API calls for each project to get their job numbers
      const jobNumberPromises = projectIds.map(async (projectId) => {
        try {
          const response = await axios.get(`/api/job-numbers?project_id=${projectId}`, { withCredentials: true });
          console.log(`🔍 Job numbers for project ${projectId}:`, response.data);
          return response.data;
        } catch (err) {
          console.error(`Error fetching job numbers for project ${projectId}:`, err);
          return [];
        }
      });

      // Wait for all API calls to complete
      const jobNumberArrays = await Promise.all(jobNumberPromises);

      // Flatten the arrays and remove duplicates
      const allJobNumbers = jobNumberArrays.flat();
      const uniqueJobNumbers = [...new Set(allJobNumbers)].filter(jobNumber => jobNumber && jobNumber.trim() !== '');

      console.log('🔍 All job numbers from selected projects:', allJobNumbers);
      console.log('🔍 Unique job numbers:', uniqueJobNumbers);

      // Auto-select all job numbers from selected projects
      setSelectedJobNumber(uniqueJobNumbers);

      console.log('✅ Auto-selected job numbers:', uniqueJobNumbers);
    } catch (err) {
      console.error('Error fetching job numbers for multiple projects:', err);
    }
  }, []);

  const fetchAllJobNumbers = useCallback(async () => {
    try {
      const response = await axios.get('/api/project-job-numbers', { withCredentials: true });
      setAllJobNumbers(response.data); // Update all job numbers for search
    } catch (err) {
      console.error('Error fetching all job numbers:', err);
    }
  }, []);

  const fetchProjectUsers = useCallback(async (projectId) => {
    try {
      const response = await axios.get(`/api/project-users?project_id=${projectId}`, { withCredentials: true });
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching project users:', err);
    }
  }, []);

  const fetchAllUsers = useCallback(async () => {
    try {
      const response = await axios.get('/api/users', { withCredentials: true });
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching all users:', err);
    }
  }, []);

  // Now define useEffect hooks that use the functions
  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch job numbers and users when project changes
  useEffect(() => {
    console.log('🔄 Project selection changed:', selectedProject);
    console.log('🔄 Projects data available:', projects.length > 0);

    // Only proceed if we have projects data loaded
    if (projects.length === 0) {
      console.log('⏳ Waiting for projects data to load...');
      return;
    }

    if (selectedProject.length === 1) {
      console.log('📋 Fetching job numbers for single project:', selectedProject[0]);
      // If only one project is selected, fetch its specific data
      fetchJobNumbers(selectedProject[0]);
      fetchProjectUsers(selectedProject[0]);
    } else if (selectedProject.length > 1) {
      console.log('📋 Fetching job numbers for multiple projects:', selectedProject);
      // If multiple projects are selected, fetch job numbers for those projects
      fetchJobNumbersForMultipleProjects(selectedProject);
      fetchAllUsers();
    } else {
      console.log('📋 Fetching all job numbers (no projects selected)');
      // If no projects are selected, fetch all data and ensure job numbers are cleared
      fetchAllJobNumbers();
      fetchAllUsers();
      setSelectedJobNumber([]); // Ensure job numbers are cleared when no projects selected
    }
  }, [selectedProject, projects, fetchJobNumbers, fetchProjectUsers, fetchJobNumbersForMultipleProjects, fetchAllJobNumbers, fetchAllUsers]);

  const handleProjectChange = (event) => {
    const projectIds = event.target.value;
    console.log('🎯 Project selection changed - Raw value:', projectIds);
    console.log('🎯 Project selection type:', typeof projectIds, 'Length:', projectIds?.length);

    // Filter job numbers FIRST, before updating selectedProject
    // This ensures job numbers are cleared before useEffect runs
    if (!projectIds || projectIds.length === 0) {
      // No projects selected - clear all job numbers
      setSelectedJobNumber([]);
      console.log('🎯 Cleared all job numbers (no projects selected)');
    } else {
      // Some projects selected - keep only job numbers from selected projects
      const selectedProjectIds = projectIds.map(id => id.toString());
      const validJobNumbers = selectedJobNumber.filter(jobNum => {
        const projectWithJobNumber = projects.find(p => p.job_number === jobNum);
        return projectWithJobNumber && selectedProjectIds.includes(projectWithJobNumber.id.toString());
      });

      if (validJobNumbers.length !== selectedJobNumber.length) {
        setSelectedJobNumber(validJobNumbers);
        console.log('🎯 Filtered job numbers to match selected projects:', validJobNumbers);
      }
    }

    // Update selectedProject AFTER filtering job numbers
    // This prevents useEffect from running before job numbers are cleared
    setSelectedProject(projectIds);
    setSelectedUser(''); // Reset user when project changes
  };

  const handleUserChange = (event) => {
    setSelectedUser(event.target.value);
  };

  const handleProjectStageChange = (event) => {
    setSelectedProjectStage(event.target.value);
  };

  const handleDepartmentChange = (event) => {
    setSelectedDepartment(event.target.value);
  };

  const handleDeliverableChange = (event) => {
    setSelectedDeliverable(event.target.value);
  };

  const handleActivityStartDateChange = (event) => {
    setActivityStartDate(event.target.value);
  };

  const handleActivityEndDateChange = (event) => {
    setActivityEndDate(event.target.value);
  };

  const handleReportTypeChange = (event) => {
    const newReportType = event.target.value;
    setSelectedReportType(newReportType);
    
    // Clear financial inputs when changing report types
    setReportFinancials({
      invoicedAmount: '',
      directorComments: ''
    });
  };

  // Admins can always see report types to get overview reports (no filters required)
  const hasFiltersSelected = () => {
    return true;
  };

  const handlePreviewOpen = async (type, title) => {
    setPreviewType(type);
    setPreviewTitle(title);

    try {
      // Build query parameters including financial inputs
      const params = new URLSearchParams();
      if (selectedProject.length > 0) params.append('project_id', selectedProject.join(','));
      if (selectedUser) params.append('user_id', selectedUser);
      if (selectedJobNumber.length > 0) params.append('job_number', selectedJobNumber.join(','));
      if (selectedProjectStage) params.append('project_stage_id', selectedProjectStage);
      if (selectedDepartment) params.append('department_id', selectedDepartment);
      if (selectedJobCode) params.append('job_code_id', selectedJobCode);
      if (selectedDeliverable) params.append('deliverable', selectedDeliverable);
      if (activityStartDate) params.append('start_date', activityStartDate);
      if (activityEndDate) params.append('end_date', activityEndDate);

      // Add financial inputs for applicable reports
      const reportConfig = reportTypes.find(rt => rt.value === type);
      if (reportConfig?.needsFinancials) {
        if (reportConfig.needsFinancials.includes('invoice') && reportFinancials.invoicedAmount) {
          params.append('invoiced_amount', reportFinancials.invoicedAmount);
        }
        if (reportConfig.needsFinancials.includes('comments') && reportFinancials.directorComments) {
          params.append('director_comments', reportFinancials.directorComments);
        }
      }

      // Determine the correct API endpoint based on report type
      let apiEndpoint;
      switch (type) {
        case 'project_resource_cost':
          apiEndpoint = '/api/reports/project-resource-cost';
          break;
        case 'project_cost_with_overhead':
          apiEndpoint = '/api/reports/project-cost-with-overhead';
          break;
        case 'invoice_vs_cost_analysis':
          apiEndpoint = '/api/reports/invoice-vs-cost-analysis';
          break;
        case 'activity_duration_trends':
          apiEndpoint = '/api/reports/activity-duration-trends';
          break;
        case 'wps_missing_entries':
          apiEndpoint = '/api/reports/wps-missing-entries';
          break;
        default:
          // For any other report types, use the preview endpoint
          console.log('Using default preview endpoint for type:', type);
          apiEndpoint = '/api/reports/preview';
      }

      console.log('🔄 Making API call:', `${apiEndpoint}?${params.toString()}`);
      
      const response = await axios.get(`${apiEndpoint}?${params.toString()}`, { withCredentials: true });

      console.log('✅ API Response received:', {
        status: response.status,
        dataKeys: Object.keys(response.data || {}),
        hasData: !!(response.data?.data || response.data?.length),
        sampleData: response.data
      });

      // Handle different response formats from the new report endpoints
      if (type === 'project_resource_cost') {
        setPreviewData(response.data || []);
      } else if (type === 'project_cost_with_overhead') {
        // Always include the response data and financial inputs, even if summary is missing
        // This handles both single project and multiple project views
        console.log('🔧 Project Cost with Overhead Response:', response.data);
        setPreviewData({
          ...response.data,
          financialInputs: reportFinancials,
          // Ensure we have a summary object even if it's missing
          summary: response.data.summary || {
            total_cost: 0,
            total_overhead_amount: 0,
            total_with_overhead: 0
          }
        });
      } else if (type === 'invoice_vs_cost_analysis') {
        // Keep the full response for analysis reports as they have nested structure
        // Include financial inputs for display
        setPreviewData({
          ...response.data,
          financialInputs: reportFinancials
        });
      } else if (type === 'activity_duration_trends' && response.data.tasks) {
        setPreviewData(response.data);
      } else if (type === 'wps_missing_entries') {
        console.log('📊 WPS Missing Entries Response:', {
          hasData: !!response.data?.data,
          dataLength: response.data?.data?.length || 0,
          hasSummary: !!response.data?.summary,
          summary: response.data?.summary
        });
        setPreviewData(response.data);
      } else {
        setPreviewData(response.data || []);
      }

      setPreviewOpen(true);
    } catch (err) {
      console.error('Error fetching preview data:', err);
      setPreviewData([]);
      setPreviewOpen(true);
    }
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
  };

  const handleDownload = async (fileFormat, type) => {
    try {
      // Build query parameters including financial inputs
      const params = new URLSearchParams();
      params.append('type', type);
      if (selectedProject.length > 0) params.append('project_id', selectedProject.join(','));
      if (selectedUser) params.append('user_id', selectedUser);
      if (selectedJobNumber) params.append('job_number', selectedJobNumber);
      if (selectedProjectStage) params.append('project_stage_id', selectedProjectStage);
      if (selectedDepartment) params.append('department_id', selectedDepartment);
      if (selectedJobCode) params.append('job_code_id', selectedJobCode);
      if (selectedDeliverable) params.append('deliverable', selectedDeliverable);
      if (activityStartDate) params.append('start_date', activityStartDate);
      if (activityEndDate) params.append('end_date', activityEndDate);

      // Add financial inputs for applicable reports
      const reportConfig = reportTypes.find(rt => rt.value === type);
      if (reportConfig?.needsFinancials) {
        if (reportConfig.needsFinancials.includes('invoice') && reportFinancials.invoicedAmount) {
          params.append('invoiced_amount', reportFinancials.invoicedAmount);
        }
        if (reportConfig.needsFinancials.includes('comments') && reportFinancials.directorComments) {
          params.append('director_comments', reportFinancials.directorComments);
        }
      }

      // Create download URL
      const downloadUrl = `/api/reports/${fileFormat}?${params.toString()}`;

      // Create temporary link and trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', ''); // Let the server determine filename
      
      // Add authentication header by using axios for proper token handling
      const response = await axios.get(downloadUrl, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      });
      
      if (response.status !== 200) {
        throw new Error(`Download failed: ${response.statusText}`);
      }
      
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      link.download = `${type}_report.xlsx`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading report:', err);
      alert('Error downloading report. Please try again.');
    }
  };

  // Function to render preview table based on report type
  const renderPreviewTable = () => {
    if (!previewData) {
      return (
        <Typography variant="body1" sx={{ p: 2, textAlign: 'center' }}>
          No data available for the selected filters.
        </Typography>
      );
    }

    // Handle different data structures
    const dataToCheck = Array.isArray(previewData) ? previewData :
                       previewData.data || previewData.employees || previewData.tasks || previewData.revisions || [];

    console.log('📊 Preview Data Debug:', {
      previewData,
      dataToCheck,
      previewType,
      hasData: dataToCheck.length > 0,
      hasSummary: !!previewData.summary
    });

    if (dataToCheck.length === 0 && !previewData.summary) {
      return (
        <Typography variant="body1" sx={{ p: 2, textAlign: 'center' }}>
          No data available for the selected filters. Try adjusting your filter criteria or leave filters empty to see all data.
        </Typography>
      );
    }

    // Different table structures based on report type
    switch (previewType) {
      case 'project_resource_cost':
        if (previewData.data && previewData.summary) {
          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                Project Resource Cost Analysis
              </Typography>
              
              {/* Summary Statistics */}
              <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Summary Statistics
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2">Total Entries: <strong>{previewData.summary.total_entries}</strong></Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2">Normal Hours: <strong>{formatHoursDisplay(previewData.summary.total_normal_hours)}</strong></Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2">Outside Normal: <strong>{formatHoursDisplay(previewData.summary.total_outside_normal_hours)}</strong></Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2">Total Cost: <strong>{previewData.summary.total_cost?.toFixed(2) || 0}</strong></Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2">Departments: <strong>{previewData.summary.departments_involved?.join(', ') || 'None'}</strong></Typography>
                  </Grid>
                </Grid>
              </Box>

              <TableContainer 
                component={Paper}
                sx={{ 
                  width: '100%',
                  overflowX: 'auto'
                }}
              >
                <Table 
                  sx={{ 
                    minWidth: 1200,
                    '& .MuiTableCell-root': {
                      whiteSpace: 'nowrap',
                      minWidth: 100
                    }
                  }}
                >
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#2D3A45' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 100 }}>Job No.</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 120 }}>User</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 140 }}>Project</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 120 }}>Deliverable</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 120 }}>Project Stage</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 120 }}>Department</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 110 }}>Normal Hours</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 120 }}>Outside Normal</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 100 }}>Total Hours</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 100 }}>Hourly Rate</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 100 }}>Total Cost</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 110 }}>Activity Date</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 110 }}>WPS Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.data.map((item, index) => (
                      <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: '#f9f9f9' } }}>
                        <TableCell>{item.job_number || 'N/A'}</TableCell>
                        <TableCell>{item.user_name}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.project_name}</Typography>
                        </TableCell>
                        <TableCell>{item.deliverable || 'N/A'}</TableCell>
                        <TableCell>{item.project_stage_name || 'N/A'}</TableCell>
                        <TableCell>{item.department_name || 'No Department Assigned'}</TableCell>
                        <TableCell>{formatHoursDisplay(item.normal_hours)}</TableCell>
                        <TableCell>{formatHoursDisplay(item.outside_normal_hours)}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{formatHoursDisplay(item.total_hours)}</TableCell>
                        <TableCell>{item.hourly_rate?.toFixed(2) || 0}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#2D3A45' }}>{item.total_cost?.toFixed(2) || 0}</TableCell>
                        <TableCell>{item.activity_date}</TableCell>
                        <TableCell>
                          <Chip
                            label={item.wps_status || (item.from_wps ? 'From WPS' : 'Timesheet')}
                            size="small"
                            color={item.wps_status === 'completed' ? 'success' : item.from_wps ? 'info' : 'default'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          );
        }
        break;

      case 'project_cost_with_overhead':
        if (previewData.summary || previewData.projects || previewData.detailed_costs) {
          const isMultiProject = !previewData.report_info?.single_project_view;
          
          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                {isMultiProject 
                  ? 'All Projects - Cost with Overhead Analysis'
                  : `${previewData.project?.name || 'Project'} - Cost with Overhead`
                }
              </Typography>
              
              {/* Show Project Overhead Information */}
              {isMultiProject && previewData.projects && previewData.projects.length > 0 && (
                <Box sx={{ mb: 2, p: 2, bgcolor: '#e8f5e8', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    Using overhead percentages stored in project settings
                  </Typography>
                </Box>
              )}

              {/* Project Breakdown for Multi-Project View */}
              {isMultiProject && previewData.projects && previewData.projects.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Project Breakdown
                  </Typography>
                  <TableContainer component={Paper} sx={{ mb: 2, maxHeight: 400, overflow: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#2D3A45' }}>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 100 }}>Job No.</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 120 }}>User</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 140 }}>Project</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 110 }}>Deliverable</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 100 }}>Department</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 80 }}>Hours</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 80 }}>Rate</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 90 }}>Base Cost</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 90 }}>Overhead %</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 100 }}>Total w/ O/H</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 100 }}>Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {previewData.detailed_costs && previewData.detailed_costs.map((item, index) => {
                          // Use the pre-calculated values from the backend
                          const baseCost = item.base_cost || 0;
                          const overheadPercentage = item.overhead_percentage || 0;
                          const totalWithOverhead = item.total_cost_with_overhead || 0;

                          return (
                            <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: '#f9f9f9' } }}>
                              <TableCell>{item.job_number || 'N/A'}</TableCell>
                              <TableCell>{item.user_name}</TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.project_name}</Typography>
                              </TableCell>
                              <TableCell>{item.project_stage_name || item.deliverable || 'N/A'}</TableCell>
                              <TableCell>{item.department_name || 'N/A'}</TableCell>
                              <TableCell>{formatHoursDisplay(item.total_hours)}</TableCell>
                              <TableCell>{item.hourly_rate?.toFixed(2) || 0}</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>{baseCost.toFixed(2)}</TableCell>
                              <TableCell sx={{ color: '#2D3A45', fontWeight: 600 }}>{overheadPercentage.toFixed(1)}%</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: '#0d47a1' }}>{totalWithOverhead.toFixed(2)}</TableCell>
                              <TableCell>{item.activity_date}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Single Project Summary Table */}
              {!isMultiProject && (
              <TableContainer sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Metric</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>Resource Cost (No Overhead)</TableCell>
                        <TableCell align="right">{previewData.summary?.total_base_cost?.toFixed(2) || 0}</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>Overhead Amount ({previewData.summary?.average_overhead_percentage?.toFixed(1) || 0}%)</TableCell>
                        <TableCell align="right">{previewData.summary?.total_overhead_amount?.toFixed(2) || 0}</TableCell>
                    </TableRow>
                    <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                      <TableCell><strong>Total Office Cost</strong></TableCell>
                        <TableCell align="right"><strong>{previewData.summary?.total_cost_with_overhead?.toFixed(2) || 0}</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              )}

              {/* Detailed Costs - Show first few entries */}
              {previewData.detailed_costs && previewData.detailed_costs.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Detailed Entries
                  </Typography>
                  <TableContainer component={Paper} sx={{ maxHeight: 400, overflow: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#2D3A45' }}>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 100 }}>Job No.</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 120 }}>User</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 140 }}>Project</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 110 }}>Deliverable</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 100 }}>Department</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 80 }}>Hours</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 80 }}>Rate</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 90 }}>Base Cost</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 90 }}>Overhead %</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 100 }}>Total w/ O/H</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 100 }}>Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {previewData.detailed_costs.map((item, index) => {
                          // Use the pre-calculated values from the backend
                          const baseCost = item.base_cost || 0;
                          const overheadPercentage = item.overhead_percentage || 0;
                          const totalWithOverhead = item.total_cost_with_overhead || 0;

                          return (
                            <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: '#f9f9f9' } }}>
                              <TableCell>{item.job_number || 'N/A'}</TableCell>
                              <TableCell>{item.user_name}</TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.project_name}</Typography>
                              </TableCell>
                              <TableCell>{item.project_stage_name || item.deliverable || 'N/A'}</TableCell>
                              <TableCell>{item.department_name || 'N/A'}</TableCell>
                              <TableCell>{formatHoursDisplay(item.total_hours)}</TableCell>
                              <TableCell>R{item.hourly_rate?.toFixed(2) || 0}</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>R{baseCost.toFixed(2)}</TableCell>
                              <TableCell sx={{ color: '#2D3A45', fontWeight: 600 }}>{overheadPercentage.toFixed(1)}%</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: '#0d47a1' }}>R{totalWithOverhead.toFixed(2)}</TableCell>
                              <TableCell>{item.activity_date}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          );
        }
        return (
          <Typography variant="body1" sx={{ p: 2, textAlign: 'center' }}>
            No project cost data available. The database may need to be populated with time entries and project information.
          </Typography>
        );

      case 'invoice_vs_cost_analysis':
        if (previewData.summary) {
          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                {previewData.project?.name || 'All Projects'} - Invoice versus actual project cost
              </Typography>
              
              {/* Show Financial Inputs Used */}
              {previewData.financialInputs && (
                <Box sx={{ mb: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    Financial Information Used:
                  </Typography>
                  {previewData.financialInputs.invoicedAmount && (
                    <Typography variant="body2">
                      • Invoiced Amount: {parseFloat(previewData.financialInputs.invoicedAmount).toFixed(2)}
                    </Typography>
                  )}
                  {previewData.financialInputs.directorComments && (
                    <Typography variant="body2">
                      • Director Comments: {previewData.financialInputs.directorComments}
                    </Typography>
                  )}
                </Box>
              )}

              <TableContainer sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Metric</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Director Comments</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>Number of Projects</TableCell>
                      <TableCell align="right">{previewData.summary.total_projects || 0}</TableCell>
                      <TableCell rowSpan={7} sx={{ verticalAlign: 'top', p: 2 }}>
                        {previewData.financialInputs?.directorComments || (
                          <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#666' }}>
                            [Space for director comments on invoicing status]
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Resource Cost</TableCell>
                      <TableCell align="right">{previewData.summary.total_base_cost?.toFixed(2) || 0}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Overhead (Average)</TableCell>
                      <TableCell align="right">{previewData.summary.total_overhead_amount?.toFixed(2) || 0}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Total Office Cost</TableCell>
                      <TableCell align="right">{previewData.summary.total_cost_with_overhead?.toFixed(2) || 0}</TableCell>
                    </TableRow>
                    <TableRow sx={{ backgroundColor: '#e8f5e8' }}>
                      <TableCell><strong>Invoiced Amount</strong></TableCell>
                      <TableCell align="right">
                        <strong>{previewData.financialInputs?.invoicedAmount
                          ? parseFloat(previewData.financialInputs.invoicedAmount).toFixed(2)
                          : previewData.summary.total_invoiced_amount?.toFixed(2) || 0}</strong>
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ backgroundColor: previewData.summary.total_variance >= 0 ? '#e8f5e8' : '#ffeaea' }}>
                      <TableCell><strong>Profit/Loss</strong></TableCell>
                      <TableCell align="right">
                        <strong>{previewData.summary.total_variance?.toFixed(2) || 0}</strong>
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ backgroundColor: previewData.summary.total_variance >= 0 ? '#e8f5e8' : '#ffeaea' }}>
                      <TableCell><strong>Profit Margin</strong></TableCell>
                      <TableCell align="right">
                        <strong>
                          {(previewData.financialInputs?.invoicedAmount || previewData.summary.total_invoiced_amount) > 0
                            ? ((previewData.summary.total_variance / (previewData.financialInputs?.invoicedAmount || previewData.summary.total_invoiced_amount)) * 100).toFixed(2)
                            : 0}%
                        </strong>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Projects Summary - Show when multiple projects */}
              {previewData.data && previewData.data.length > 1 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Projects Summary
                  </Typography>

                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#2D3A45' }}>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 100 }}>Job No.</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 200 }}>Project</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 140 }}>Department</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 100 }}>Hours</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 120 }}>Resource Cost</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 120 }}>Overhead</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 130 }}>Total Office Cost</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 130 }}>Invoiced Amount</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 110 }}>Profit/Loss</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 100 }}>Margin</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {previewData.data.map((project, index) => {
                          const profitLoss = project.variance || 0;
                          const isInvoiced = project.invoiced_amount && project.invoiced_amount > 0;
                          const margin = isInvoiced && project.total_cost_with_overhead > 0
                            ? (profitLoss / project.invoiced_amount) * 100
                            : project.variance_percentage || 0;

                          return (
                            <TableRow
                              key={index}
                              sx={{
                                '&:nth-of-type(odd)': { bgcolor: '#f9f9f9' }
                              }}
                            >
                              <TableCell>{project.job_number || 'N/A'}</TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {project.project_name || 'N/A'}
                                </Typography>
                              </TableCell>
                              <TableCell>{project.department_name || 'N/A'}</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>{formatHoursDisplay(project.total_hours)}</TableCell>
                              <TableCell>R{(project.base_cost || 0).toFixed(2)}</TableCell>
                              <TableCell>R{(project.overhead_amount || 0).toFixed(2)}</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>
                                R{(project.total_cost_with_overhead || 0).toFixed(2)}
                              </TableCell>
                              <TableCell>
                                R{(project.invoiced_amount || 0).toFixed(2)}
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>
                                R{(profitLoss || 0).toFixed(2)}
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>
                                {margin.toFixed(2)}%
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Detailed Cost Breakdown */}
              {previewData.cost_details && previewData.cost_details.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Detailed Cost Breakdown
                  </Typography>
                  
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f0f0f0' }}>
                          <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Job No.</TableCell>
                          <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Project</TableCell>
                          <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>User</TableCell>
                          <TableCell sx={{ fontWeight: 600, minWidth: 140 }}>Deliverable</TableCell>
                          <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Department</TableCell>
                          <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>Hours</TableCell>
                          <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>Rate</TableCell>
                          <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>Cost</TableCell>
                          <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {previewData.cost_details.map((detail, index) => (
                          <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: '#fafafa' } }}>
                            <TableCell>{detail.job_number || 'N/A'}</TableCell>
                            <TableCell>{detail.project_name || 'N/A'}</TableCell>
                            <TableCell>{detail.user_name || 'N/A'}</TableCell>
                            <TableCell>{detail.deliverable || 'N/A'}</TableCell>
                            <TableCell>{detail.project_stage_name || 'N/A'}</TableCell>
                            <TableCell>{detail.department_name || 'N/A'}</TableCell>
                            <TableCell>{formatHoursDisplay(detail.total_hours)}</TableCell>
                            <TableCell>{detail.hourly_rate?.toFixed(2) || 0}</TableCell>
                            <TableCell>{detail.base_cost?.toFixed(2) || 0}</TableCell>
                            <TableCell>{detail.activity_date || 'N/A'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          );
        }
        return (
          <Typography variant="body1" sx={{ p: 2, textAlign: 'center' }}>
            No invoice versus actual project cost data available.
          </Typography>
        );

      case 'activity_duration_trends':
        if (previewData.tasks) {
          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                Activity Duration Trends Analysis
              </Typography>
              
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f0f0f0' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Job No.</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Project</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Deliverable</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Planned Hours</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Actual Hours</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.tasks.map((task, index) => {
                      console.log('Task row:', index, task); // Debug log
                      return (
                        <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: '#fafafa' } }}>
                          <TableCell>{task.job_number || 'N/A'}</TableCell>
                          <TableCell>{task.user_name}</TableCell>
                          <TableCell>{task.project_name}</TableCell>
                          <TableCell>{task.deliverable || 'N/A'}</TableCell>
                          <TableCell>{task.department_name || 'N/A'}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>
                            {formatHoursDisplay(task.planned_hours)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                            {formatHoursDisplay(task.actual_hours)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          );
        }
        return (
          <Typography variant="body1" sx={{ p: 2, textAlign: 'center' }}>
            No activity trends data available.
          </Typography>
        );

      case 'wps_missing_entries':
        if (previewData.data && previewData.summary) {
          // --- Cumulative totals: sum of the existing per-line figures, grouped by user/project ---
          // Grouping adapts to the User filter: when a user is selected, group by project only;
          // otherwise group by user + project. All filters are already applied to previewData.data.
          const userFiltered = !!selectedUser;
          const totalsMap = new Map();
          previewData.data.forEach(item => {
            const userName = item.user_name || 'Unknown User';
            const projectName = item.project_name || 'Unknown Project';
            const key = userFiltered ? projectName : `${userName}${projectName}`;
            const existing = totalsMap.get(key) || {
              user_name: userName,
              project_name: projectName,
              job_number: item.job_number || '',
              entries: 0,
              planned_hours: 0,
              actual_hours: 0
            };
            existing.entries += 1;
            existing.planned_hours += parseFloat(item.planned_hours_total) || 0;
            existing.actual_hours += parseFloat(item.actual_duration) || 0;
            if (!existing.job_number && item.job_number) existing.job_number = item.job_number;
            totalsMap.set(key, existing);
          });
          const cumulativeTotals = Array.from(totalsMap.values());
          const cumulativeTotalsRow = {
            entries: cumulativeTotals.reduce((s, r) => s + r.entries, 0),
            planned_hours: cumulativeTotals.reduce((s, r) => s + r.planned_hours, 0),
            actual_hours: cumulativeTotals.reduce((s, r) => s + r.actual_hours, 0)
          };
          // unique_projects_affected is not returned by the backend summary; compute from data
          const uniqueProjectsCount = new Set(previewData.data.map(d => d.project_name)).size;

          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                WPS Missing Time Entries
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                WPS entries where actual hours have not been updated. Same detection logic as WPS notifications.
              </Typography>

              {/* Summary Statistics */}
              <Box sx={{ mb: 3, p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ffe0b2' }}>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2">Missing Entries: <strong>{previewData.summary.total_missing_entries}</strong></Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2">Users Affected: <strong>{previewData.summary.unique_users_affected}</strong></Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2">Projects Affected: <strong>{uniqueProjectsCount}</strong></Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2">Oldest Entry: <strong>{previewData.summary.oldest_entry_days} day(s)</strong></Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Chip label={`Critical: ${previewData.summary.critical_count}`} size="small" sx={{ bgcolor: '#ffebee', color: '#c62828', fontWeight: 600 }} />
                  </Grid>
                  <Grid item xs={4}>
                    <Chip label={`Urgent: ${previewData.summary.urgent_count}`} size="small" sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600 }} />
                  </Grid>
                  <Grid item xs={4}>
                    <Chip label={`Normal: ${previewData.summary.normal_count}`} size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }} />
                  </Grid>
                </Grid>
              </Box>

              {/* Cumulative Totals - sum of the per-line figures grouped by user/project */}
              {cumulativeTotals.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ color: '#2D3A45', fontWeight: 600, mb: 1 }}>
                    Cumulative Totals ({userFiltered ? 'by Project' : 'by User & Project'})
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Sum of the missing entries below, rolled up {userFiltered ? 'per project' : 'per user and project'}.
                  </Typography>
                  <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#3D4A55' }}>
                          {!userFiltered && <TableCell sx={{ color: 'white', fontWeight: 600 }}>User</TableCell>}
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Job No.</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Project</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">Entries</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">Planned Hours</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">Actual Hours</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cumulativeTotals.map((row, idx) => (
                          <TableRow key={idx} sx={{ '&:nth-of-type(odd)': { bgcolor: '#fafafa' } }}>
                            {!userFiltered && <TableCell sx={{ fontWeight: 500 }}>{row.user_name}</TableCell>}
                            <TableCell>{row.job_number || 'N/A'}</TableCell>
                            <TableCell>{row.project_name}</TableCell>
                            <TableCell align="right">{row.entries}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>{formatHoursDisplay(row.planned_hours)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: '#2e7d32' }}>{formatHoursDisplay(row.actual_hours)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow sx={{ bgcolor: '#2D3A45' }}>
                          <TableCell colSpan={userFiltered ? 2 : 3} sx={{ color: 'white', fontWeight: 700 }}>TOTAL</TableCell>
                          <TableCell align="right" sx={{ color: 'white', fontWeight: 700 }}>{cumulativeTotalsRow.entries}</TableCell>
                          <TableCell align="right" sx={{ color: 'white', fontWeight: 700 }}>{formatHoursDisplay(cumulativeTotalsRow.planned_hours)}</TableCell>
                          <TableCell align="right" sx={{ color: 'white', fontWeight: 700 }}>{formatHoursDisplay(cumulativeTotalsRow.actual_hours)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 1100, '& .MuiTableCell-root': { whiteSpace: 'nowrap', minWidth: 90 } }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#2D3A45' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Job No.</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>User</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Department</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Project</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">Planned Hours</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">Actual Hours</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Week Start</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Timesheet</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">Days Old</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Urgency</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.data.map((item, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          '&:nth-of-type(odd)': { bgcolor: '#fafafa' },
                          bgcolor: item.urgency === 'critical' ? '#fff8e1' : item.urgency === 'urgent' ? '#fff3e0' : 'inherit'
                        }}
                      >
                        <TableCell>{item.job_number || 'N/A'}</TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{item.user_name}</TableCell>
                        <TableCell>{item.department_name}</TableCell>
                        <TableCell>{item.project_name}</TableCell>
                        <TableCell align="right">{formatHoursDisplay(item.planned_hours_total)}</TableCell>
                        <TableCell align="right">{formatHoursDisplay(item.actual_duration)}</TableCell>
                        <TableCell>{item.week_start || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip
                            label={item.status || 'draft'}
                            size="small"
                            color={item.status === 'submitted' ? 'info' : item.status === 'approved' ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={item.timesheet_filled ? 'Filled' : 'Not Filled'}
                            size="small"
                            color={item.timesheet_filled ? 'success' : 'error'}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {item.days_since_created}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={item.urgency}
                            size="small"
                            sx={{
                              bgcolor: item.urgency === 'critical' ? '#d32f2f' : item.urgency === 'urgent' ? '#f57c00' : '#388e3c',
                              color: 'white',
                              fontWeight: 600,
                              textTransform: 'capitalize'
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          );
        }
        return (
          <Typography variant="body1" sx={{ p: 2, textAlign: 'center' }}>
            No WPS missing entries data available.
          </Typography>
        );

      default:
        return (
          <Typography variant="body1" sx={{ p: 2, textAlign: 'center' }}>
            Preview not available for this report type.
          </Typography>
        );
    }
  };

  // Function to render the selected report card
  const renderSelectedReportCard = () => {
    if (!selectedReportType) return null;

    const reportConfig = reportTypes.find(type => type.value === selectedReportType);
    if (!reportConfig) return null;

    // Check if this report needs financial inputs
    const needsFinancials = reportConfig.needsFinancials && reportConfig.needsFinancials.length > 0;

    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ color: '#2D3A45', mb: 3 }}>
          Generate Report
        </Typography>

        <Paper sx={{
          p: 3,
          borderRadius: '16px',
          boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.15)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          border: '1px solid rgba(45, 58, 69, 0.1)'
        }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#2D3A45' }}>
            {reportConfig.label}
          </Typography>
          <Typography variant="body2" gutterBottom sx={{ mb: 3 }}>
            {reportConfig.description}
          </Typography>

          {/* Conditional Financial Input Fields */}
          {needsFinancials && (
            <Box sx={{ mb: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e9ecef' }}>
              <Typography variant="subtitle1" gutterBottom sx={{ color: '#2D3A45', fontWeight: 600 }}>
                Financial Information Required
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: '#6c757d' }}>
                Please provide the following information to generate this report:
              </Typography>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(auto-fit, minmax(250px, 1fr))' }, gap: 2 }}>


                {/* Invoiced Amount Input */}
                {reportConfig.needsFinancials.includes('invoice') && (
                  <TextField
                    label="Actual Amount Invoiced"
                    type="number"
                    value={reportFinancials.invoicedAmount}
                    onChange={(e) => setReportFinancials({
                      ...reportFinancials,
                      invoicedAmount: e.target.value
                    })}
                    inputProps={{ min: 0, step: 0.01 }}
                    helperText="Enter the actual invoiced amount (manually filled)"
                    fullWidth
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1, color: '#6c757d' }}>R</Typography>
                    }}
                  />
                )}
              </Box>

              {/* Director Comments (full width) */}
              {reportConfig.needsFinancials.includes('comments') && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    label="Director Comments"
                    multiline
                    rows={3}
                    value={reportFinancials.directorComments}
                    onChange={(e) => setReportFinancials({
                      ...reportFinancials,
                      directorComments: e.target.value
                    })}
                    placeholder="Enter director comments regarding invoicing status..."
                    helperText="Space for director to comment on invoicing status and project progress"
                    fullWidth
                  />
                </Box>
              )}
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={() => handlePreviewOpen(selectedReportType, reportConfig.label)}
              sx={{
                bgcolor: '#2D3A45',
                '&:hover': { bgcolor: '#3D4A55' }
              }}
            >
              Preview
            </Button>
            <Button
              variant="contained"
              onClick={() => handleDownload('excel', selectedReportType)}
              sx={{
                bgcolor: '#2D3A45',
                '&:hover': { bgcolor: '#3D4A55' }
              }}
            >
              Download Excel
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  };

  // Only allow admin access to reports
  console.log('📊 Reports - Admin check:', isAdmin());
  if (!isAdmin()) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '60vh', 
        textAlign: 'center' 
      }}>
        <Typography variant="h4" sx={{ color: '#2D3A45', mb: 2 }}>
          Access Restricted
        </Typography>
        <Typography variant="body1" sx={{ color: '#5A6A75', mb: 3 }}>
          Only administrators can access the Reports section.
        </Typography>
        <Typography variant="body2" sx={{ color: '#8A9BA8' }}>
          Please contact your system administrator if you need access to reports.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{
        mb: 4,
        pb: 2,
        borderBottom: '1px solid rgba(0,0,0,0.1)',
        backgroundImage: 'url(/geometric-pattern.svg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        borderRadius: 1,
        p: 3,
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255,255,255,0.85)',
          zIndex: 0
        }} />
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h4" gutterBottom sx={{ color: '#2D3A45', fontWeight: 600 }}>
            Reports
          </Typography>
          <Typography variant="subtitle1" sx={{ color: '#5A6A75' }} gutterBottom>
            Generate and download detailed reports for projects, timesheets, and invoice backup documentation
          </Typography>
        </Box>
      </Box>

      {/* Filter Section */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: '16px', boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.15)' }}>
        <Typography variant="h6" gutterBottom sx={{ color: '#2D3A45', mb: 2 }}>
          Report Filters (All Optional)
        </Typography>
        <Typography variant="body2" sx={{ color: '#5A6A75', mb: 3, fontStyle: 'italic' }}>
          Leave filters empty to see all available data. Use filters to narrow down results to specific criteria.
        </Typography>

        {/* First Row - Project, Job Number, User */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          {/* Project Dropdown - Multiple Selection */}
          <FormControl sx={{ minWidth: 200, flex: 1 }}>
            <InputLabel id="project-select-label">Project(s)</InputLabel>
            <Select
              labelId="project-select-label"
              value={selectedProject}
              onChange={handleProjectChange}
              label="Project(s)"
              multiple
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return 'All Projects';
                }
                if (selected.length === 1) {
                  const project = projects.find(p => p.id === parseInt(selected[0]));
                  return project?.name || 'Unknown Project';
                }
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.slice(0, 2).map((value) => {
                      const project = projects.find(p => p.id === parseInt(value));
                      return (
                        <Chip
                          key={value}
                          label={project?.name || 'Unknown'}
                          size="small"
                          sx={{ height: 20, fontSize: '0.75rem' }}
                        />
                      );
                    })}
                    {selected.length > 2 && (
                      <Chip
                        label={`+${selected.length - 2} more`}
                        size="small"
                        sx={{ height: 20, fontSize: '0.75rem' }}
                      />
                    )}
                  </Box>
                );
              }}
            >
              {console.log('📊 Projects in dropdown:', projects) || projects.map((project) => (
                <MenuItem
                  key={project.id}
                  value={project.id.toString()}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(255, 152, 0, 0.3)', // Light orange
                      '&:hover': {
                        backgroundColor: 'rgba(255, 152, 0, 0.4)', // Slightly darker orange on hover
                      },
                    },
                  }}
                >
                  {project.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Job Number TextField with Autocomplete */}
          <Box sx={{ minWidth: 200, flex: 1, position: 'relative' }}>
            <TextField
              fullWidth
              label="Job Number(s)"
              value={jobNumberInput}
              onChange={(e) => {
                const value = e.target.value;
                setJobNumberInput(value);
              }}
              placeholder="Type to search job numbers"
              size="medium"
            />
            {jobNumberInput && filteredJobNumbers.length > 0 && (
              <Paper sx={{ position: 'absolute', top: '100%', left: 0, right: 0, maxHeight: 200, overflow: 'auto', zIndex: 1000, mt: 1 }}>
                {filteredJobNumbers.map((jobNum, index) => (
                  <Box
                    key={index}
                    onClick={() => {
                      // Add to selected job numbers if not already there
                      if (!selectedJobNumber.includes(jobNum)) {
                        setSelectedJobNumber([...selectedJobNumber, jobNum]);

                        // Find the project with this job number and add it to selectedProject
                        const projectWithJobNumber = projects.find(p => p.job_number === jobNum);
                        if (projectWithJobNumber && !selectedProject.includes(projectWithJobNumber.id.toString())) {
                          setSelectedProject([...selectedProject, projectWithJobNumber.id.toString()]);
                        }
                      }
                      setJobNumberInput('');
                    }}
                    sx={{
                      p: 1.5,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      borderBottom: '1px solid #eee'
                    }}
                  >
                    {jobNum}
                  </Box>
                ))}
              </Paper>
            )}
            {selectedJobNumber.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selectedJobNumber.map((jobNum) => (
                  <Chip
                    key={jobNum}
                    label={jobNum}
                    onDelete={() => {
                      // Remove the job number
                      setSelectedJobNumber(selectedJobNumber.filter(j => j !== jobNum));

                      // Find and remove the associated project
                      const projectWithJobNumber = projects.find(p => p.job_number === jobNum);
                      if (projectWithJobNumber) {
                        setSelectedProject(selectedProject.filter(id => id !== projectWithJobNumber.id.toString()));
                      }
                    }}
                    size="small"
                  />
                ))}
              </Box>
            )}
          </Box>

          {/* User Dropdown - Show all users for admin, only current user for regular users */}
          <FormControl sx={{ minWidth: 200, flex: 1 }}>
            <InputLabel id="user-select-label">User</InputLabel>
            <Select
              labelId="user-select-label"
              value={selectedUser}
              onChange={handleUserChange}
              label="User"
              disabled={!isAdmin()} // Disable for non-admin users
            >
              {isAdmin() && <MenuItem value="">All Users</MenuItem>}
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Second Row - Project Stage, Department, Deliverable */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          {/* Project Stage Dropdown */}
          <FormControl sx={{ minWidth: 200, flex: 1 }}>
            <InputLabel id="project-stage-select-label">Project Stage</InputLabel>
            <Select
              labelId="project-stage-select-label"
              value={selectedProjectStage}
              onChange={handleProjectStageChange}
              label="Project Stage"
            >
              <MenuItem value="">All Project Stages</MenuItem>
              {projectStages.map((stage) => (
                <MenuItem key={stage.id} value={stage.id}>
                  {stage.stage_number > 6 ? stage.name : `Stage ${stage.stage_number}: ${stage.name}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Department Dropdown */}
          <FormControl sx={{ minWidth: 200, flex: 1 }}>
            <InputLabel id="department-select-label">Department</InputLabel>
            <Select
              labelId="department-select-label"
              value={selectedDepartment}
              onChange={handleDepartmentChange}
              label="Department"
            >
              <MenuItem value="">All Departments</MenuItem>
              {departments.map((dept) => (
                <MenuItem key={dept.id} value={dept.id}>
                  {dept.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Deliverable TextField */}
          <TextField
            sx={{ minWidth: 200, flex: 1 }}
            fullWidth
            label="Deliverable"
            value={selectedDeliverable}
            onChange={handleDeliverableChange}
            placeholder="Enter deliverable"
            variant="outlined"
          />
        </Box>

        {/* Third Row - Activity-Specific Date Range */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: '#2D3A45', fontWeight: 500 }}>
            Activity Date Range (Optional)
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              label="Activity Start Date (Optional)"
              type="date"
              value={activityStartDate}
              onChange={handleActivityStartDateChange}
              InputLabelProps={{ shrink: true }}
              sx={{ backgroundColor: 'white' }}
              helperText="Leave empty to include all dates from the beginning"
            />
            
            <TextField
              fullWidth
              label="Activity End Date (Optional)"
              type="date"
              value={activityEndDate}
              onChange={handleActivityEndDateChange}
              InputLabelProps={{ shrink: true }}
              sx={{ backgroundColor: 'white' }}
              helperText="Leave empty to include all dates until now"
            />
          </Box>
        </Box>

        {/* Report Type Selection - Only show if filters are selected */}
        {hasFiltersSelected() && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#2D3A45', mb: 2 }}>
              Select Report Type
            </Typography>
            <FormControl fullWidth>
              <InputLabel id="report-type-select-label">Report Type</InputLabel>
              <Select
                labelId="report-type-select-label"
                value={selectedReportType}
                onChange={handleReportTypeChange}
                label="Report Type"
              >
                <MenuItem value="">Select a report type...</MenuItem>
                {reportTypes.map((reportType) => (
                  <MenuItem key={reportType.value} value={reportType.value}>
                    {reportType.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Show description of selected report type */}
            {selectedReportType && (
              <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ color: '#5A6A75' }}>
                  {reportTypes.find(type => type.value === selectedReportType)?.description}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* Note: Reports are admin-only, so no filter requirements */}
      
      {/* Admin message */}
      {!selectedReportType && (
        <Box sx={{ mt: 4, textAlign: 'center', p: 4 }}>
          <Typography variant="h6" sx={{ color: '#2D3A45', mb: 2 }}>
            Admin Dashboard - All Reports Available
          </Typography>
          <Typography variant="body2" sx={{ color: '#5A6A75', mb: 2 }}>
            As an administrator, you can access all report types and see all data by default. 
            Use the optional filters above to narrow down results to specific projects, users, or time periods.
          </Typography>
          <Typography variant="body2" sx={{ color: '#8A9BA8' }}>
            Select a report type above to get started.
          </Typography>
        </Box>
      )}

      {/* Render selected report card */}
      {renderSelectedReportCard()}

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={handlePreviewClose}
        maxWidth="xl"
        fullWidth
        disableRestoreFocus
        PaperProps={{
          sx: {
            width: '95vw',
            height: '90vh',
            maxWidth: 'none',
            maxHeight: 'none',
            m: 1
          }
        }}
      >
        <DialogTitle>
          {previewTitle} Preview
        </DialogTitle>
        <DialogContent 
          dividers={true}
          sx={{
            p: 0,
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px'
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
              borderRadius: '4px'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#888',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: '#555'
              }
            }
          }}
        >
          <Box sx={{ p: 2, minWidth: 'max-content' }}>
          {renderPreviewTable()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handlePreviewClose}
            sx={{
              color: '#FF5722',
              '&:hover': {
                color: '#FF7A50',
                bgcolor: 'rgba(255, 87, 34, 0.04)'
              }
            }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={() => handleDownload('excel', previewType)}
            sx={{
              bgcolor: '#2D3A45',
              '&:hover': { bgcolor: '#3D4A55' }
            }}
          >
            Download Excel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReportsPage;
